import { useMemo, useState } from "react";
import OfferFormModal from "@/pages/admin/offers/components/OfferFormModal";
import { useOffersData } from "@/hooks/useOffers";
import { supabase } from "@/lib/supabase";
import { logAudit } from "@/lib/audit";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import type { Offer } from "@/types";

const currency = new Intl.NumberFormat("es-DO", {
  style: "currency",
  currency: "DOP",
  minimumFractionDigits: 2,
});

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
}

interface OfferStatus {
  key: "active" | "scheduled" | "expired" | "inactive";
  label: string;
  tone: string;
}

function getOfferStatus(offer: Offer): OfferStatus {
  const today = todayISO();
  if (!offer.active) {
    return { key: "inactive", label: "Inactiva", tone: "bg-background-200 text-foreground-600" };
  }
  if (offer.start_date && offer.start_date > today) {
    return { key: "scheduled", label: "Programada", tone: "bg-secondary-100 text-secondary-900" };
  }
  if (offer.end_date && offer.end_date < today) {
    return { key: "expired", label: "Vencida", tone: "bg-primary-100 text-primary-700" };
  }
  return { key: "active", label: "Activa", tone: "bg-accent-100 text-accent-900" };
}

function describeDiscount(offer: Offer): string {
  if (offer.discount_type === "percentage") return `${Number(offer.discount_value ?? 0)}% de descuento`;
  if (offer.discount_type === "fixed") return `${currency.format(Number(offer.discount_value ?? 0))} de descuento`;
  return `Precio especial ${currency.format(Number(offer.special_price ?? 0))}`;
}

export default function AdminOffersPage() {
  const toast = useToast();
  const { profile } = useAuth();
  const { offers, products, categories, loading, error, reload } = useOffersData();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Offer | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const offersWithStatus = useMemo(
    () => offers.map((offer) => ({ offer, status: getOfferStatus(offer) })),
    [offers],
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return offersWithStatus.filter(({ offer, status }) => {
      if (statusFilter !== "all" && status.key !== statusFilter) return false;
      if (!term) return true;
      const target = offer.scope === "product" ? offer.product_name : offer.category_name;
      return `${offer.name} ${target ?? ""}`.toLowerCase().includes(term);
    });
  }, [offersWithStatus, search, statusFilter]);

  const stats = useMemo(() => {
    const active = offersWithStatus.filter((o) => o.status.key === "active").length;
    const scheduled = offersWithStatus.filter((o) => o.status.key === "scheduled").length;
    const expired = offersWithStatus.filter((o) => o.status.key === "expired").length;
    return { total: offers.length, active, scheduled, expired };
  }, [offers, offersWithStatus]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (offer: Offer) => {
    setEditing(offer);
    setFormOpen(true);
  };

  const handleToggleActive = async (offer: Offer) => {
    setTogglingId(offer.id);
    const { error: updateError } = await supabase
      .from("offers")
      .update({ active: !offer.active, updated_at: new Date().toISOString() })
      .eq("id", offer.id);
    setTogglingId(null);
    if (updateError) {
      toast.error("No se pudo actualizar la oferta.");
      return;
    }
    await logAudit({
      userId: profile?.id ?? null,
      userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
      action: "editar",
      entity: "offers",
      entityId: offer.id,
      branchId: profile?.branch_id ?? null,
      oldValue: { active: offer.active },
      newValue: { active: !offer.active },
    });
    toast.success(offer.active ? "Oferta desactivada." : "Oferta activada.");
    reload();
  };

  const handleDelete = async (offer: Offer) => {
    setTogglingId(offer.id);
    const { error: deleteError } = await supabase.from("offers").delete().eq("id", offer.id);
    setTogglingId(null);
    if (deleteError) {
      toast.error("No se pudo eliminar la oferta.");
      return;
    }
    await logAudit({
      userId: profile?.id ?? null,
      userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
      action: "eliminar",
      entity: "offers",
      entityId: offer.id,
      branchId: profile?.branch_id ?? null,
      oldValue: { nombre: offer.name },
    });
    toast.success("Oferta eliminada.");
    reload();
  };

  const statCards = [
    { key: "total", label: "Ofertas", value: stats.total, icon: "ri-price-tag-3-line", tone: "primary" },
    { key: "active", label: "Activas ahora", value: stats.active, icon: "ri-fire-line", tone: "accent" },
    { key: "scheduled", label: "Programadas", value: stats.scheduled, icon: "ri-calendar-schedule-line", tone: "secondary" },
    { key: "expired", label: "Vencidas", value: stats.expired, icon: "ri-timer-line", tone: "secondary" },
  ];

  const toneClass: Record<string, string> = {
    primary: "bg-primary-100 text-primary-700",
    accent: "bg-accent-100 text-accent-800",
    secondary: "bg-secondary-200 text-secondary-900",
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950">Ofertas y Descuentos</h1>
          <p className="mt-1.5 text-sm text-foreground-500">
            Descuento por producto o categoría con vigencia. El POS aplica la oferta activa automáticamente.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-add-line text-base"></i>
          Nueva oferta
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {statCards.map((card) => (
          <div
            key={card.key}
            className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3"
          >
            <span className={`flex h-9 w-9 items-center justify-center rounded-md ${toneClass[card.tone]}`}>
              <i className={`${card.icon} text-base`}></i>
            </span>
            <div>
              <p className="font-heading text-lg font-bold leading-none text-foreground-950">{card.value}</p>
              <p className="mt-1 text-[11px] text-foreground-500">{card.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base"></i>
          </span>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar oferta por nombre, producto o categoría..."
            className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        >
          <option value="all">Todos los estados</option>
          <option value="active">Activas</option>
          <option value="scheduled">Programadas</option>
          <option value="expired">Vencidas</option>
          <option value="inactive">Inactivas</option>
        </select>
      </div>

      {error ? (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 p-4 text-sm text-primary-800 sm:flex-row sm:items-center sm:justify-between">
          <span className="flex items-center gap-2">
            <i className="ri-error-warning-line text-base"></i>
            {error}
          </span>
          <button
            type="button"
            onClick={reload}
            className="whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-primary-700"
          >
            Reintentar
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center gap-3 rounded-lg border border-background-200 bg-background-100 py-14 text-sm text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-lg"></i>
          Cargando ofertas...
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-14 text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-price-tag-3-line text-2xl"></i>
          </span>
          <p className="text-sm font-semibold text-foreground-800">Sin ofertas</p>
          <p className="max-w-sm text-xs text-foreground-500">
            Crea una oferta por producto o categoría. El POS la mostrará y aplicará cuando esté vigente.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map(({ offer, status }) => {
            const target =
              offer.scope === "product"
                ? offer.product_name ?? "Producto"
                : offer.category_name ?? "Categoría";
            return (
              <div
                key={offer.id}
                className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <span
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${
                        offer.scope === "product"
                          ? "bg-primary-100 text-primary-700"
                          : "bg-accent-100 text-accent-800"
                      }`}
                    >
                      <i
                        className={`${offer.scope === "product" ? "ri-shopping-bag-3-line" : "ri-apps-2-line"} text-xl`}
                      ></i>
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-foreground-950">{offer.name}</p>
                      <p className="truncate text-[11px] text-foreground-500">
                        {offer.scope === "product" ? "Producto" : "Categoría"}: {target}
                      </p>
                    </div>
                  </div>
                  <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${status.tone}`}>
                    {status.label}
                  </span>
                </div>

                <div className="rounded-md border border-background-200 bg-background-50 px-3.5 py-3">
                  <p className="font-heading text-base font-bold text-foreground-950">
                    {describeDiscount(offer)}
                  </p>
                  <p className="mt-1 flex items-center gap-1.5 text-[11px] text-foreground-500">
                    <i className="ri-calendar-line text-sm"></i>
                    {formatDate(offer.start_date)} — {formatDate(offer.end_date)}
                  </p>
                </div>

                <div className="mt-auto flex items-center justify-end gap-2 border-t border-background-200 pt-3">
                  <button
                    type="button"
                    onClick={() => openEdit(offer)}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition-colors hover:bg-background-100"
                  >
                    <i className="ri-edit-2-line text-base"></i>
                  </button>
                  <button
                    type="button"
                    disabled={togglingId === offer.id}
                    onClick={() => handleToggleActive(offer)}
                    className={`flex h-9 w-9 items-center justify-center rounded-md border transition-colors disabled:opacity-60 ${
                      offer.active
                        ? "border-primary-200 bg-primary-50 text-primary-700"
                        : "border-accent-300 bg-accent-50 text-accent-900"
                    }`}
                  >
                    <i
                      className={`${offer.active ? "ri-toggle-line" : "ri-toggle-fill"} text-base`}
                    ></i>
                  </button>
                  <button
                    type="button"
                    disabled={togglingId === offer.id}
                    onClick={() => handleDelete(offer)}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-primary-200 bg-primary-50 text-primary-700 transition-colors hover:bg-primary-100 disabled:opacity-60"
                  >
                    <i className="ri-delete-bin-6-line text-base"></i>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <OfferFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={reload}
        offer={editing}
        products={products}
        categories={categories}
      />
    </div>
  );
}